/**
 * Utilidad para suprimir errores conocidos que no afectan la funcionalidad
 * pero pueden ser molestos en el desarrollo
 */

/**
 * Suprime los warnings de ResizeObserver que son comunes en aplicaciones React/Ionic
 * y no afectan la funcionalidad de la aplicación
 */
export const suppressResizeObserverErrors = (): void => {
  // Interceptar errores de ResizeObserver
  const originalError = window.console.error;
  
  window.console.error = (...args: any[]) => {
    const message = args.join(' ');
    
    // Lista de mensajes de error que queremos suprimir
    const suppressedMessages = [
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      'Non-passive event listener',
      'Failed to construct \'URL\': Invalid base URL',
      'Invalid base URL',
      'getAssetPath',
      'getNamedUrl',
    ];
    
    // Si el mensaje contiene alguno de los patrones a suprimir, no lo mostramos
    const shouldSuppress = suppressedMessages.some(pattern => 
      message.includes(pattern)
    );
    
    if (!shouldSuppress) {
      originalError.apply(console, args);
    }
  };

  // Interceptar errores no manejados
  window.addEventListener('error', (event) => {
    const message = event.message || '';
    const shouldSuppress = [
      'ResizeObserver loop',
      'Invalid base URL',
      'Failed to construct \'URL\'',
      'getAssetPath',
      'getNamedUrl'
    ].some(pattern => message.includes(pattern));

    if (shouldSuppress) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  });

  // Interceptar promesas rechazadas
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.message) {
      const message = event.reason.message;
      const shouldSuppress = [
        'ResizeObserver loop',
        'Invalid base URL',
        'Failed to construct \'URL\'',
        'getAssetPath',
        'getNamedUrl'
      ].some(pattern => message.includes(pattern));

      if (shouldSuppress) {
        event.preventDefault();
        return false;
      }
    }
  });
};

/**
 * Restaura el comportamiento original de console.error
 */
export const restoreConsoleError = (): void => {
  // Esta función podría usarse en tests o en casos específicos
  // donde queremos ver todos los errores
  if ((window.console.error as any).__original) {
    window.console.error = (window.console.error as any).__original;
  }
};

/**
 * Parche específico para Ionic que puede ayudar con ResizeObserver
 */
export const patchIonicResizeObserver = (): void => {
  // Parche para ResizeObserver si no está disponible nativamente
  if (typeof window !== 'undefined' && !window.ResizeObserver) {
    window.ResizeObserver = class ResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        // Implementación mock que no hace nada
      }
      observe() {}
      unobserve() {}
      disconnect() {}
    };
  }

  // Parche para el ResizeObserver existente para evitar loops
  if (typeof window !== 'undefined' && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;

    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
          try {
            // Usar requestAnimationFrame para evitar loops
            window.requestAnimationFrame(() => {
              callback(entries, observer);
            });
          } catch (error) {
            // Suprimir errores de ResizeObserver
            if (!(error instanceof Error) || !error.message.includes('ResizeObserver loop')) {
              throw error;
            }
          }
        };
        super(wrappedCallback);
      }
    };
  }
};

/**
 * Inicializa la supresión de errores conocidos
 * Debe llamarse una sola vez al inicio de la aplicación
 */
export const initializeErrorSuppression = (): void => {
  // Solo ejecutar en desarrollo o cuando sea necesario
  if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_SUPPRESS_RESIZE_OBSERVER === 'true') {
    suppressResizeObserverErrors();
    patchIonicResizeObserver();
    console.log('🔇 Error suppression initialized - ResizeObserver warnings will be filtered');
  }
};
